import type { SvgElementProps } from '@axinom/mosaic-ui';
import { SvgElement } from '@axinom/mosaic-ui';
import React from 'react';

export enum IconName {
  Chevron,
  ExpandMenu,
  Home,
  ShowMoreDots,
}

const DEFAULT_PATH_PROPS = {
  vectorEffect: 'non-scaling-stroke' as const,
  fill: 'none',
};

/**
 * Breadcrumb arrow
 * @example
 * <Chevron />
 */
const Chevron: React.FC<SvgElementProps> = (props) => (
  <SvgElement viewBox="0 0 20 31" enableBackground="new 0 0 20 31" {...props}>
    <path
      {...DEFAULT_PATH_PROPS}
      stroke="#FFFFFF"
      strokeWidth="3"
      strokeLinecap="square"
      d="M3,2.9l14.2,11.9L3,27.9"
    />
  </SvgElement>
);

/**
 * ExpandMenu icon
 * @example
 * <ExpandMenu />
 */
const ExpandMenu: React.FC<SvgElementProps> = (props) => (
  <SvgElement viewBox="0 0 40 40" {...props}>
    <path
      {...DEFAULT_PATH_PROPS}
      stroke="var(--mosaic-secondary, #1478af)"
      strokeWidth="2"
      d="M4,9.8h32 M4,20h32 M4,30.2h32"
    />
  </SvgElement>
);

/**
 * Home icon
 * @example
 * <Home />
 */
const Home: React.FC<SvgElementProps> = (props) => (
  <SvgElement viewBox="0 0 40 40" {...props}>
    <path
      {...DEFAULT_PATH_PROPS}
      strokeWidth="2"
      d="M39.2,19.4L19.7,2.4l-6.8,6.3V3.8H8.2v8.9l-7.4,6.8
	 M34.8,22.8L19.8,10L5,22.8v14.8h9.9v-9.1h9.8v9.1h10.1L34.8,22.8z"
    />
  </SvgElement>
);

/**
 * Breadcrumb show more dots
 * @example
 * <ShowMoreIcon />
 */
const ShowMoreDots: React.FC<SvgElementProps> = (props) => (
  <SvgElement viewBox="0 0 21 4" {...props}>
    <g>
      <path
        {...DEFAULT_PATH_PROPS}
        d="M2.2,3.5c-0.4,0-0.7-0.1-0.9-0.4s-0.4-0.6-0.4-1c0-0.4,0.1-0.7,0.4-0.9s0.6-0.4,0.9-0.4S2.8,1,3.1,1.3
		c0.3,0.3,0.4,0.6,0.4,0.9c0,0.4-0.1,0.7-0.4,1C2.8,3.4,2.5,3.5,2.2,3.5z"
      />
      <path
        {...DEFAULT_PATH_PROPS}
        d="M10.5,3.5c-0.4,0-0.7-0.1-0.9-0.4s-0.4-0.6-0.4-1c0-0.4,0.1-0.7,0.4-0.9s0.6-0.4,0.9-0.4s0.7,0.1,0.9,0.4
		c0.3,0.3,0.4,0.6,0.4,0.9c0,0.4-0.1,0.7-0.4,1C11.2,3.4,10.9,3.5,10.5,3.5z"
      />
      <path
        {...DEFAULT_PATH_PROPS}
        d="M18.8,3.5c-0.4,0-0.7-0.1-0.9-0.4s-0.4-0.6-0.4-1c0-0.4,0.1-0.7,0.4-0.9s0.6-0.4,0.9-0.4s0.7,0.1,0.9,0.4
		c0.3,0.3,0.4,0.6,0.4,0.9c0,0.4-0.1,0.7-0.4,1C19.5,3.4,19.2,3.5,18.8,3.5z"
      />
    </g>
  </SvgElement>
);

export interface IconsProps extends SvgElementProps {
  icon: IconName;
}

export const Icons: React.FC<IconsProps> = ({ icon, ...props }) => {
  const iconComponents = {
    [IconName.Chevron]: Chevron,
    [IconName.ExpandMenu]: ExpandMenu,
    [IconName.Home]: Home,
    [IconName.ShowMoreDots]: ShowMoreDots,
  };

  const IconComponent = iconComponents[icon];
  return <IconComponent {...props} />;
};
